import {QUIKLEARN_V2_BACKEND_URL} from 'config/app.config';

const DEFAULT_TUTOR_NAME = 'Benjamin Franklin';
const DEFAULT_HISTORICAL_FIGURE = 'Benjamin Franklin';
const MAX_HISTORY_TURNS = 20;

const getBaseUrl = () =>
  String(QUIKLEARN_V2_BACKEND_URL || '').replace(/\/$/, '');

function firstNonEmptyTrimmed(...candidates) {
  for (let i = 0; i < candidates.length; i += 1) {
    const s = String(candidates[i] ?? '').trim();
    if (s) return s;
  }
  return '';
}

/**
 * Backend responses may nest fields under `data` and/or use alternate names.
 */
function extractTutorFields(payload) {
  const root = payload && typeof payload === 'object' ? payload : {};
  const nested = root.data && typeof root.data === 'object' ? root.data : {};

  const tutorResponse = firstNonEmptyTrimmed(
    nested.tutorResponse,
    nested.tutor_response,
    nested.reply,
    nested.answer,
    root.tutorResponse,
    root.tutor_response,
  );

  const transcribedMessage = firstNonEmptyTrimmed(
    nested.transcribedMessage,
    nested.transcribed_message,
    nested.transcription,
    nested.transcript,
    nested.userMessage,
    nested.user_message,
    nested.userInput,
    nested.user_input,
    root.transcribedMessage,
    root.transcribed_message,
    root.transcription,
    root.transcript,
  );

  return {tutorResponse, transcribedMessage};
}

function mapErrorMessage(status, fallbackMessage) {
  switch (status) {
    case 400:
      return 'Something went wrong. Please try again.';
    case 401:
      return 'Your session has expired. Please sign back in.';
    case 413:
      return 'That recording is too long. Please record a shorter clip.';
    case 415:
      return 'That audio format is not supported. Please try again.';
    case 502:
      return 'The tutor is having trouble responding. Please try again.';
    case 500:
      return 'The tutor is unavailable right now. Please try again shortly.';
    default:
      return fallbackMessage || `Tutor request failed (${status}).`;
  }
}

/**
 * POST /v2/tutor (or /v2/tutor/stream when `streaming: true`) — single-shot
 * Q&A endpoint that accepts either typed text or a recorded audio blob.
 * Returns `{ tutorResponse, transcribedMessage }` (transcribedMessage is
 * empty for text-only turns). Conversation history is owned by the caller
 * and sent on every request.
 *
 * `streaming` toggles the internal GPT + DeepRails streaming pipeline on
 * the backend. The response envelope is identical; APIGW REST still buffers
 * so the client contract does not change.
 */
export async function askTutor({
  token,
  userInput,
  userAudio,
  audioFilename = 'question.webm',
  tutorName = DEFAULT_TUTOR_NAME,
  historicalFigure = DEFAULT_HISTORICAL_FIGURE,
  messageHistory = [],
  currentContext = [],
  streaming = false,
}) {
  if (!token) {
    throw new Error('Missing auth token for tutor request.');
  }
  if (!userInput && !userAudio) {
    throw new Error('Tutor request needs either userInput or userAudio.');
  }

  const form = new FormData();
  if (userInput) form.append('userInput', userInput);
  if (userAudio) form.append('userAudio', userAudio, audioFilename);
  form.append('tutorName', tutorName);
  form.append('historicalFigure', historicalFigure);
  form.append(
    'messageHistory',
    JSON.stringify(
      Array.isArray(messageHistory)
        ? messageHistory.slice(-MAX_HISTORY_TURNS)
        : [],
    ),
  );
  form.append(
    'currentContext',
    JSON.stringify(Array.isArray(currentContext) ? currentContext : []),
  );

  const endpoint = streaming ? '/tutor/stream' : '/tutor';

  let response;
  try {
    response = await fetch(`${getBaseUrl()}${endpoint}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: form,
    });
  } catch (networkError) {
    const message =
      networkError?.message || 'Could not reach the tutor. Please try again.';
    const error = new Error(message);
    error.cause = networkError;
    throw error;
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const message = mapErrorMessage(response.status, payload?.message);
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  const {tutorResponse, transcribedMessage} = extractTutorFields(payload);
  return {tutorResponse, transcribedMessage};
}
